/**
 * Conteúdo dos cartões do kanban do setor (ambiente, lote, tarefa e PEÇA
 * DESMEMBRADA). O invólucro arrastável é do Kanban; aqui só o que o gestor lê
 * de relance: status, prazo que vale, quem está com o item e o que está
 * pendente.
 */
import React from "react";
import { FiLink, FiMessageSquare, FiScissors } from "react-icons/fi";

import { plural } from "../../../utils/dinheiro";
import { toDate } from "../../../utils/status";
import {
  ROTULO_PRIORIDADE,
  ROTULO_STATUS_AMBIENTE,
  ROTULO_STATUS_PECA,
  ROTULO_STATUS_TAREFA,
  TOM_PRIORIDADE,
  TOM_STATUS_AMBIENTE,
  TOM_STATUS_PECA,
  TOM_STATUS_TAREFA,
  tarefaAtrasada,
} from "../dados/vocabulario";
import {
  pessoasDaDemanda,
  pessoasDaPeca,
  textoDaOrigem,
  textoDasPecasFora,
  textoDoPrazo,
  textoDoTempoNoSetor,
} from "./modelo";
import { Avatares, Selos } from "./styles";
import { AvatarPessoa, PillStatus } from "./ui";

const MAX_AVATARES = 3;
const MAX_AMBIENTES_NO_LOTE = 3;

function Prazo({ data, hoje, origem, titulo }) {
  const { texto, tom } = textoDoPrazo(data, hoje);
  return (
    <span className={tom === "neutral" ? undefined : tom} title={titulo}>
      {texto}
    </span>
  );
}

function Pessoas({ pessoas, atividades }) {
  if (!pessoas.length) return null;
  const nomes = pessoas.map((p) => p.nome).join(", ");
  return (
    <div className="pessoas" title={nomes}>
      <Avatares>
        {pessoas.slice(0, MAX_AVATARES).map((p) => {
          const atividade = atividades?.get(p.id);
          return (
            <AvatarPessoa
              key={p.id}
              nome={p.nome}
              tamanho={22}
              tom={atividade?.tom || "neutral"}
            />
          );
        })}
      </Avatares>
      <span className="nomes">{nomes}</span>
      {pessoas.some((p) => p.origem === "planilha" && p.vinculado) && (
        <FiLink size={12} aria-label="Vinculado ao nome da planilha" />
      )}
    </div>
  );
}

export function ConteudoAmbiente({ demanda, indice, atividades, hoje }) {
  const pessoas = pessoasDaDemanda(demanda, indice);
  const tempo = textoDoTempoNoSetor(demanda);
  const tarefas = demanda.tarefas || [];
  const pecasFora = textoDasPecasFora(demanda.pecasFora);
  const temSelos =
    demanda.pendencias.length > 0 || tarefas.length > 0 || demanda.motivoPausa || pecasFora || demanda.aguardandoEtapa || demanda.servico;
  return (
    <>
      <div className="linha1">
        <span className="codigo">{demanda.pedido || "s/ pedido"}</span>
        <span className="titulo">{demanda.ambiente || "Sem ambiente"}</span>
        <PillStatus tom={TOM_STATUS_AMBIENTE[demanda.statusApp]}>
          {ROTULO_STATUS_AMBIENTE[demanda.statusApp]}
        </PillStatus>
      </div>
      <div className="sub">
        {[demanda.cliente, demanda.lote && `Lote ${demanda.lote}`].filter(Boolean).join(" · ") || "Sem cliente"}
      </div>
      <div className="meta">
        <Prazo
          data={demanda.prazoEfetivo}
          hoje={hoje}
          titulo={
            demanda.origemPrazo === "app" ? "Prazo definido no Mapa (vale no lugar do da planilha)" : undefined
          }
        />
        {tempo && <span>{tempo}</span>}
      </div>
      <Pessoas pessoas={pessoas} atividades={atividades} />
      {temSelos && (
        <Selos>
          {demanda.motivoPausa && <PillStatus tom="warning" ponto={false}>{demanda.motivoPausa}</PillStatus>}
          {pecasFora && (
            <PillStatus tom="info" ponto={false} title="Peças desmembradas circulando fora do ambiente">
              {pecasFora}
            </PillStatus>
          )}
          {tarefas.length > 0 && (
            <PillStatus tom="neutral" ponto={false}>{plural(tarefas.length, "tarefa", "tarefas")}</PillStatus>
          )}
          {demanda.servico && <PillStatus tom="info" ponto={false}>Serviço</PillStatus>}
          {demanda.aguardandoEtapa && (
            // Está nesta sala à espera de uma etapa que não tem sala própria
            // (ex.: MDF ainda não comprado, na porta do corte).
            <PillStatus tom="warning" ponto={false}>Aguardando {demanda.aguardandoEtapa}</PillStatus>
          )}
          {demanda.pendencias.map((p) => (
            <PillStatus key={p.codigo} tom="warning" ponto={false}>{p.texto}</PillStatus>
          ))}
        </Selos>
      )}
    </>
  );
}

export function ConteudoLote({ lote, hoje }) {
  const clientes = [...new Set(lote.demandas.map((d) => d.cliente).filter(Boolean))];
  const primeiros = lote.demandas.slice(0, MAX_AMBIENTES_NO_LOTE);
  const resto = lote.demandas.length - primeiros.length;
  const pecasFora = textoDasPecasFora(lote.pecasFora);
  let status = { tom: "neutral", texto: "Aguardando" };
  if (lote.atrasadas > 0) status = { tom: "danger", texto: plural(lote.atrasadas, "atrasado", "atrasados") };
  else if (lote.emProducao > 0) status = { tom: "info", texto: `${lote.emProducao} em produção` };
  return (
    <>
      <div className="linha1">
        <span className="titulo">{lote.rotulo}</span>
        <PillStatus tom={status.tom}>{status.texto}</PillStatus>
      </div>
      <div className="sub">
        {plural(lote.demandas.length, "ambiente", "ambientes")}
        {clientes.length ? ` · ${clientes.join(", ")}` : ""}
      </div>
      <div className="meta">
        <Prazo data={lote.prazoEfetivo} hoje={hoje} />
      </div>
      <Selos>
        {primeiros.map((d) => (
          <PillStatus key={d.chaveLinha} tom={d.atrasada ? "danger" : TOM_STATUS_AMBIENTE[d.statusApp]} ponto={false}>
            {d.rotulo || d.ambiente}
          </PillStatus>
        ))}
        {resto > 0 && <PillStatus tom="neutral" ponto={false}>+{resto}</PillStatus>}
        {pecasFora && <PillStatus tom="info" ponto={false}>{pecasFora}</PillStatus>}
      </Selos>
    </>
  );
}

export function ConteudoTarefa({ tarefa, indice, atividades, hoje }) {
  const colaborador = indice.colaboradores.get(tarefa.colaborador_id);
  const atrasada = tarefaAtrasada(tarefa, hoje);
  const peca = tarefa.peca_id ? indice.pecas.get(tarefa.peca_id) : null;
  return (
    <>
      <div className="linha1">
        <span className="codigo">{tarefa.codigo}</span>
        <span className="titulo">{tarefa.titulo}</span>
        <PillStatus tom={atrasada ? "danger" : TOM_STATUS_TAREFA[tarefa.status]}>
          {ROTULO_STATUS_TAREFA[tarefa.status]}
        </PillStatus>
      </div>
      {(tarefa.ambiente_rotulo || peca) && (
        <div className="sub">
          {[peca ? `${peca.codigo} · ${peca.nome}` : null, tarefa.ambiente_rotulo].filter(Boolean).join(" · ")}
        </div>
      )}
      <div className="meta">
        <Prazo data={toDate(tarefa.data_limite)} hoje={hoje} />
        {tarefa.comentarios_total > 0 && (
          <span title="Comentários">
            <FiMessageSquare size={12} aria-hidden="true" /> {tarefa.comentarios_total}
          </span>
        )}
      </div>
      {colaborador ? (
        <Pessoas pessoas={[{ ...colaborador, origem: "tarefa" }]} atividades={atividades} />
      ) : (
        <div className="pessoas">Sem colaborador</div>
      )}
      {tarefa.status === "pausada" && tarefa.motivo_pausa && (
        <Selos>
          <PillStatus tom="warning" ponto={false}>{tarefa.motivo_pausa}</PillStatus>
        </Selos>
      )}
    </>
  );
}

/**
 * Cartão da peça desmembrada. O ambiente de origem é o contexto (a peça só
 * existe por causa dele), e a prioridade aparece quando não é normal — com a
 * régua no `title`, porque ela pode ser herdada do ambiente ou estimada pelo
 * prazo, e não uma escolha de alguém (ADR-0059).
 */
export function ConteudoPeca({ peca, indice, atividades, hoje }) {
  const pessoas = pessoasDaPeca(peca, indice);
  const tarefas = peca.tarefas || [];
  const reguaPrioridade = `Prioridade ${textoDaOrigem(peca.origemPrioridade, { feminino: true })}`;
  const reguaPrazo = `Prazo ${textoDaOrigem(peca.origemPrazo)}`;
  return (
    <>
      <div className="linha1">
        <span className="codigo">{peca.codigo}</span>
        <span className="titulo">{peca.nome}</span>
        <PillStatus tom={peca.atrasada ? "danger" : TOM_STATUS_PECA[peca.status]}>
          {ROTULO_STATUS_PECA[peca.status]}
        </PillStatus>
      </div>
      <div className="sub">
        <FiScissors size={11} aria-hidden="true" /> {peca.ambienteRotulo || "Ambiente de origem não carregado"}
        {peca.cliente ? ` · ${peca.cliente}` : ""}
      </div>
      <div className="meta">
        <Prazo data={peca.prazoEfetivo} hoje={hoje} titulo={peca.prazoEfetivo ? reguaPrazo : undefined} />
        {tarefas.length > 0 && <span>{plural(tarefas.length, "tarefa", "tarefas")}</span>}
      </div>
      {pessoas.length > 0 ? (
        <Pessoas pessoas={pessoas} atividades={atividades} />
      ) : (
        <div className="pessoas">Sem responsável</div>
      )}
      {(peca.prioridade !== "normal" || peca.motivo_pausa) && (
        <Selos>
          {peca.prioridade !== "normal" && (
            <PillStatus tom={TOM_PRIORIDADE[peca.prioridade]} ponto={false} title={reguaPrioridade}>
              {ROTULO_PRIORIDADE[peca.prioridade]}
            </PillStatus>
          )}
          {peca.status === "pausada" && peca.motivo_pausa && (
            <PillStatus tom="warning" ponto={false}>{peca.motivo_pausa}</PillStatus>
          )}
        </Selos>
      )}
    </>
  );
}
